/**
 * CopyEltJobModal Component
 *
 * Modal for copying an existing ELT job with a new name and code.
 */

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Alert } from '../../../components/ui/Alert';
import { FormField } from '../../../components/forms/FormField';
import { useCreateEltJob } from '../hooks/useCreateEltJob';
import { eltJobService } from '../../../api/elt-job.service';
import type { EltJob, EltJobCreateDTO } from '../../../types/elt-job.types';

interface CopyEltJobModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  sourceJob: EltJob;
}

interface CopyFormData {
  eltJobName: string;
  eltJobCode: string;
}

/**
 * Generate a code from a name (lowercase, replace spaces with hyphens, strip special chars)
 */
function generateCode(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 50);
}

export const CopyEltJobModal: React.FC<CopyEltJobModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  sourceJob,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [autoGenerateCode, setAutoGenerateCode] = useState<boolean>(true);
  const [isCopying, setIsCopying] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<CopyFormData>({
    defaultValues: {
      eltJobName: `Copy of ${sourceJob.eltJobName}`,
      eltJobCode: generateCode(`Copy of ${sourceJob.eltJobName}`),
    },
  });

  const eltJobName = watch('eltJobName');

  // Auto-generate code from name
  useEffect(() => {
    if (autoGenerateCode && eltJobName) {
      setValue('eltJobCode', generateCode(eltJobName));
    }
  }, [eltJobName, autoGenerateCode, setValue]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      const defaultName = `Copy of ${sourceJob.eltJobName}`;
      reset({
        eltJobName: defaultName,
        eltJobCode: generateCode(defaultName),
      });
      setAutoGenerateCode(true);
      setErrorMessage('');
      setSuccessMessage('');
    }
  }, [isOpen, sourceJob, reset]);

  const { mutate: createJob, isPending: isCreating } = useCreateEltJob(tenantId, {
    onSuccess: () => {
      setSuccessMessage('ELT job copied successfully!');
      setErrorMessage('');
      setTimeout(() => {
        reset();
        onClose();
        setSuccessMessage('');
      }, 1000);
    },
    onError: (error) => {
      setErrorMessage(error);
      setIsCopying(false);
    },
  });

  const onSubmit = async (formData: CopyFormData) => {
    setErrorMessage('');
    setIsCopying(true);

    try {
      // Fetch full job details
      const fullJob = await eltJobService.getEltJob(tenantId, sourceJob.eltJobId);

      // Build create DTO from source job, stripping system fields and IDs
      const createData: EltJobCreateDTO = {
        eltJobCode: formData.eltJobCode,
        eltJobName: formData.eltJobName,
        description: fullJob.description,
        dataAgentId: fullJob.dataAgentId,
        dbConnectionId: fullJob.dbConnectionId,
        managedSqlDbId: fullJob.managedSqlDbId,
        cronExpression: fullJob.cronExpression,
        timezone: fullJob.timezone,
        outputFormat: fullJob.outputFormat,
        compression: fullJob.compression,
        queries: fullJob.queries.map((q) => ({
          queryName: q.queryName,
          sqlQuery: q.sqlQuery,
          orderIndex: q.orderIndex,
          isActive: q.isActive,
          landingConfig: q.landingConfig ? { ...q.landingConfig } : undefined,
        })),
        parameters: fullJob.parameters,
        pipelineConfig: {
          enabled: fullJob.pipelineConfig.enabled,
          loadBatchSize: fullJob.pipelineConfig.loadBatchSize,
          errorHandling: fullJob.pipelineConfig.errorHandling,
          stagingSteps: fullJob.pipelineConfig.stagingSteps.map((s) => ({
            name: s.name,
            description: s.description,
            orderIndex: s.orderIndex,
            isActive: s.isActive,
            sql: s.sql,
            timeout: s.timeout,
          })),
          productionSteps: fullJob.pipelineConfig.productionSteps.map((s) => ({
            name: s.name,
            description: s.description,
            orderIndex: s.orderIndex,
            isActive: s.isActive,
            sql: s.sql,
            timeout: s.timeout,
          })),
        },
      };

      createJob(createData);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to fetch job details');
      setIsCopying(false);
    }
  };

  const handleClose = () => {
    if (!isCreating && !isCopying) {
      setErrorMessage('');
      setSuccessMessage('');
      reset();
      onClose();
    }
  };

  const isPending = isCreating || isCopying;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <Modal.Header>Copy ELT Job</Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {errorMessage && <Alert variant="error">{errorMessage}</Alert>}
          {successMessage && <Alert variant="success">{successMessage}</Alert>}

          <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
            <p className="text-sm text-gray-600">
              Copying from: <span className="font-semibold">{sourceJob.eltJobName}</span>{' '}
              <span className="text-gray-400 font-mono">({sourceJob.eltJobCode})</span>
            </p>
          </div>

          <FormField label="Job Name" error={errors.eltJobName?.message} required>
            <Input
              {...register('eltJobName', {
                required: 'Job name is required',
                minLength: { value: 1, message: 'Job name is required' },
                maxLength: { value: 100, message: 'Job name must not exceed 100 characters' },
              })}
              placeholder="Enter job name"
              disabled={isPending}
            />
          </FormField>

          <FormField label="Job Code" error={errors.eltJobCode?.message} required>
            <div className="space-y-2">
              <Input
                {...register('eltJobCode', {
                  required: 'Job code is required',
                  minLength: { value: 3, message: 'Code must be at least 3 characters' },
                  maxLength: { value: 50, message: 'Code must not exceed 50 characters' },
                  pattern: {
                    value: /^[a-z0-9-]+$/,
                    message: 'Code must contain only lowercase letters, numbers, and hyphens',
                  },
                })}
                placeholder="enter-job-code"
                disabled={isPending || autoGenerateCode}
              />
              <label className="flex items-center gap-2 text-sm text-gray-500">
                <input
                  type="checkbox"
                  checked={autoGenerateCode}
                  onChange={(e) => setAutoGenerateCode(e.target.checked)}
                  disabled={isPending}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                Auto-generate from name
              </label>
            </div>
          </FormField>

          <div className="flex items-center justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" loading={isPending}>
              Copy Job
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};
